/* eslint-disable @typescript-eslint/no-non-null-assertion */
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import styles from "../Support.module.scss";
import WhatsAppColor from "@Icons/WhatsAppColor";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES, PAGE, WHATSAPP_URL } from "constants/index";
import wrapperHOC from "@Utils/wrapperHOC";
import { ReactElement, useEffect, useMemo, useState } from "react";
import { getLeadData } from "services/leadsHelper";
import { isEmptyObject } from "helpers/validations";
import LoaderIcon from "@Icons/LoaderIcon";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useIsApp from "hooks/useIsApp";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { encryptDataString } from "helpers/encryption";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
import useTranslationHook from "hooks/useTranslationHook";
import CloseIcon from "@Icons/CloseIcon";
import WhatsAppIcon from "@Icons/WhatsAppIcon";
import useIsDesktop from "hooks/useIsDesktop";
import getWeComUrlBasedOnSource from "helpers/getWeComUrlBasedOnSource";
import config from "@Config/index";

interface AgentDetails {
  name?: string;
  formatted_phone?: string;
  is_pipeline_paused?: boolean;
  contact_type?: string;
  contact_value?: string;
  students_helped?: number;
  rating?: number;
  review_count?: number;
  gender?: string;
  email?: string;
  phone_details?: Record<string, any>;
}

type ContactChannel = "whatsapp" | "wechat" | "email";

const getRandomFromArray = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const getRandomInRange = (min: number, max: number, decimals = 0): number => {
  const val = Math.random() * (max - min) + min;
  return decimals ? parseFloat(val.toFixed(decimals)) : Math.floor(val);
};

const getInitials = (name?: string): string => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
};

const getContactChannelByCountry = (country: string): ContactChannel => {
  const code = country?.toUpperCase();
  // if (CHINA_COUNTRY_CODES.includes(code)) return "wechat";
  if (["US"].includes(code)) return "email";
  return "whatsapp";
};

const AgentContainer: React.FC<{
  isPropertyPageConnectModal?: boolean;
  fallBackComp?: ReactElement;
  onClose?: () => void;
  setShowAgentContainer?: any;
}> = ({ isPropertyPageConnectModal, fallBackComp, onClose, setShowAgentContainer }) => {
  const customWindowOpen = useCustomWindowOpen();
  const { t } = useTranslationHook();
  const [agentDetails, setAgentDetails] = useState<AgentDetails>({});
  const [loading, setLoadingState] = useState(true);
  const showAgentDetails = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_AGENT_DETAILS);
  const country = useSelector(({ appData }: AppState) => appData.countryCode);
  const whatsappUrl = (val: string) => `https://wa.me/${val}`;
  const isApp = useIsApp();
  const isDesktop = useIsDesktop();

  const handleEventEmitter = ({ action, label = "", ...rest }: any) => {
    gtagEventEmitter({
      action,
      label: `${PAGE.SUPPORT} ${label}`,
      ...rest,
    });
  };

  const contactChannel = useMemo(() => getContactChannelByCountry(country), [country]);

  const fallbackValues = useMemo(
    () => ({
      studentsHelped: getRandomFromArray([1500, 1700, 1900]),
      rating: getRandomInRange(4, 4.7, 1),
      reviewCount: getRandomInRange(70, 100),
    }),
    [],
  );

  const studentsHelped = agentDetails.students_helped ?? fallbackValues.studentsHelped;
  const rating = agentDetails.rating ?? fallbackValues.rating;
  const reviewCount = agentDetails.review_count ?? fallbackValues.reviewCount;

  const onCtaClick = () => {
    switch (contactChannel) {
      case "whatsapp": {
        const formattedPhone = agentDetails?.formatted_phone;
        const urlToOpen = formattedPhone ? whatsappUrl(formattedPhone) : WHATSAPP_URL;

        handleEventEmitter({
          action: "mweb_agent_details_whatsapp_clicked",
          label: "mweb_agent_details_whatsapp_clicked",
        });
        if (isApp) {
          sendWebViewEvent(WEB_VIEW_APP_EVENTS.OPEN_EXTERNAL_URL, { url: urlToOpen });
        } else {
          customWindowOpen({ url: urlToOpen, target: "_blank", isExternal: true });
        }
        break;
      }
      case "wechat": {
        handleEventEmitter({
          action: "mweb_agent_details_wechat_clicked",
          label: "mweb_agent_details_wechat_clicked",
        });
        const { URLDweb, URLMweb } = getWeComUrlBasedOnSource();
        const weChatUrl = isDesktop ? URLDweb : URLMweb;
        if (isApp) {
          sendWebViewEvent(WEB_VIEW_APP_EVENTS.OPEN_EXTERNAL_URL, { url: weChatUrl });
        } else {
          customWindowOpen({ url: weChatUrl, target: "_blank", isExternal: true });
        }
        break;
      }
      case "email": {
        const emailAddress =
          agentDetails.email || agentDetails.contact_value || "contact@amberstudent.com";
        handleEventEmitter({
          action: "mweb_agent_details_email_clicked",
          label: "mweb_agent_details_email_clicked",
        });
        customWindowOpen({
          url: `mailto:${emailAddress}`,
          target: "_self",
          isExternal: true,
        });
        break;
      }
      default:
        break;
    }

    if (isPropertyPageConnectModal && onClose) onClose();
  };

  useEffect(() => {
    let cancelled = false;
    const fetchAgentDetails = async () => {
      const leadDataString = localStorage.getItem("lead-form-one-success-data");
      const leadData = leadDataString ? JSON.parse(leadDataString) : {};
      const encryptedLeadUUID =
        encryptDataString(leadData?.uuid, "lead_user_lead_uuid").split("=")[1] || "";
      try {
        const params = {
          methods: "contact_agent_details",
          cache: false,
          sort_key: "created",
          sort_order: "desc",
          not_state: "lost",
        };
        if (!isEmptyObject(leadData)) {
          // @ts-ignore
          const { data } = await getLeadData(encryptedLeadUUID, params);
          if (!cancelled && data?.contact_agent_details != null) {
            setAgentDetails(data.contact_agent_details);
            if (setShowAgentContainer) setShowAgentContainer(true);
            handleEventEmitter({
              action: "mweb_agent_details_viewed",
              label: "mweb_agent_details_viewed",
            });
          }
        }
        if (!cancelled) setLoadingState(false);
      } catch (error) {
        console.error("Error fetching agent details:", error);
        if (!cancelled) setLoadingState(false);
      }
    };
    fetchAgentDetails();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!showAgentDetails || config.IS_CHINA_APP) return fallBackComp || null;

  const renderStars = () => {
    const filledStars = Math.floor(rating);
    return (
      <div className={styles.agentStars}>
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8.30 2.50C8.15 2.19 7.84 2 7.50 2C7.16 2 6.85 2.19 6.70 2.50L5.12 5.68L1.61 6.20C1.27 6.25 1.00 6.48 0.89 6.81C0.78 7.14 0.87 7.50 1.11 7.74L3.65 10.22L3.05 13.71C3.00 14.05 3.14 14.39 3.41 14.60C3.69 14.80 4.06 14.83 4.36 14.67L7.50 13.02L10.64 14.67C10.94 14.83 11.31 14.80 11.59 14.60C11.86 14.39 12.00 14.05 11.95 13.71L11.36 10.22L13.89 7.74C14.13 7.50 14.22 7.14 14.11 6.81C14.00 6.48 13.73 6.25 13.39 6.20L9.88 5.68L8.30 2.50Z"
              fill={i < filledStars ? "#0E9F6E" : "#E5E7EB"}
            />
          </svg>
        ))}
        <span className={styles.agentRatingValue}>{rating.toFixed(1)}</span>
        <span className={styles.agentReviewCount}>({reviewCount})</span>
      </div>
    );
  };

  const renderCtaButton = () => {
    let label = t("support.agent.cta.whatsapp");
    let bgClass = styles.ctaWhatsapp;
    let icon = !isDesktop ? (
      <WhatsAppIcon fill="#FFFFFF" height="20" width="20" />
    ) : (
      <WhatsAppColor fill="#FFFFFF" width={20} height={20} />
    );

    if (contactChannel === "wechat") {
      label = t("support.agent.cta.wechat");
      bgClass = styles.ctaWechat;
      icon = (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M13.34 8.09c.18 0 .36.01.53.02-.48-2.24-2.87-3.9-5.6-3.9C5.14 4.21 2.5 6.4 2.5 9.14c0 1.58.86 2.87 2.29 3.87l-.57 1.72 2-1.01c.72.14 1.29.29 2 .29.17 0 .35-.01.52-.03a3.88 3.88 0 0 1-.16-1.1c0-2.64 2.23-4.79 4.76-4.79zm-3.07-1.8c.43 0 .71.29.71.71 0 .43-.28.72-.71.72-.43 0-.86-.29-.86-.72 0-.42.43-.71.86-.71zm-3.87.71c-.43 0-.86-.29-.86-.71 0-.43.43-.72.86-.72.43 0 .71.29.71.72 0 .42-.28.71-.71.71z"
            fill="white"
          />
          <path
            d="M17.5 12.77c0-2.29-2.29-4.15-4.86-4.15-2.72 0-4.87 1.86-4.87 4.15s2.15 4.15 4.87 4.15c.57 0 1.14-.14 1.72-.29l1.57.86-.43-1.43c1.14-.86 2-2 2-3.29zm-6.44-.72c-.29 0-.57-.28-.57-.57 0-.29.28-.57.57-.57.43 0 .72.28.72.57 0 .29-.29.57-.72.57zm3.15 0c-.29 0-.57-.28-.57-.57 0-.29.28-.57.57-.57.43 0 .71.28.71.57 0 .29-.28.57-.71.57z"
            fill="white"
          />
        </svg>
      );
    } else if (contactChannel === "email") {
      label = t("support.agent.cta.email");
      bgClass = styles.ctaEmail;
      icon = (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M3.33 4.17h13.34c.92 0 1.66.74 1.66 1.66v8.34c0 .92-.74 1.66-1.66 1.66H3.33c-.92 0-1.66-.74-1.66-1.66V5.83c0-.92.74-1.66 1.66-1.66z"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.33 5.83L10 10.83 1.67 5.83"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }

    return (
      <button
        type="button"
        className={formatClassNames(styles.agentCtaButton, bgClass)}
        onClick={onCtaClick}
        aria-label={label}
      >
        {icon}
        <span>{label}</span>
      </button>
    );
  };

  return !isEmptyObject(agentDetails) ? (
    <div className={formatClassNames(styles.agentContainer)}>
      {isPropertyPageConnectModal ? (
        <div className={styles.agentCardMobile}>
          <div className={styles.agentAccentBar} />
          <div className={styles.agentCardMobileContent}>
            <button
              type="button"
              className={styles.agentCloseBtn}
              onClick={onClose}
              aria-label="Close"
            >
              <CloseIcon size={16} />
            </button>
            <div className={styles.agentAvatarLarge}>
              <span className={styles.agentInitialsLarge}>{getInitials(agentDetails?.name)}</span>
              <div className={formatClassNames(styles.agentOnlineDotLarge)} />
            </div>
            <span className={styles.agentNameLarge}>{agentDetails?.name}</span>
            {renderStars()}
            <div className={styles.agentExperience}>
              <span>
                {t("support.agent.helped")} <b>{studentsHelped.toLocaleString()}</b>{" "}
                {t("support.agent.students")}
              </span>
            </div>
            {renderCtaButton()}
          </div>
        </div>
      ) : (
        <>
          <div className={styles.headingContainer}>
            <p className={styles.heading}>{t("support.agent.heading")}</p>
          </div>
          <div className={formatClassNames(styles.agentCard)}>
            <div className={styles.agentAccentBar} />
            <div className={styles.agentCardContent}>
              <div className={styles.agentProfileRow}>
                <div className={styles.agentAvatar}>
                  <span className={styles.agentInitials}>{getInitials(agentDetails?.name)}</span>
                  <div className={formatClassNames(styles.agentOnlineDot)} />
                </div>
                <div className={styles.agentInfo}>
                  <span className={styles.agentName}>{agentDetails?.name}</span>
                  {renderStars()}
                </div>
              </div>
              <div className={styles.agentExperience}>
                <span>
                  {t("support.agent.helped")} <b>{studentsHelped.toLocaleString()}</b>{" "}
                  {t("support.agent.students")}
                </span>
              </div>
              {renderCtaButton()}
            </div>
          </div>
        </>
      )}
    </div>
  ) : (
    <>
      {loading && isPropertyPageConnectModal ? (
        <div className={styles.loading}>
          <LoaderIcon />
          {t("support.agent.loading")}
        </div>
      ) : (
        fallBackComp || null
      )}
    </>
  );
};

export default wrapperHOC(AgentContainer, {
  componentName: "AgentContainer-Support",
  showForChina: true,
});
